import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Send, ShoppingCart, Plus, Trash2, Pencil, Package } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import ProgressBar from "@/components/quote/ProgressBar";
import StepProduct from "@/components/quote/StepProduct";
import StepLayout from "@/components/quote/StepLayout";
import StepDimensions from "@/components/quote/StepDimensions";
import StepMaterial from "@/components/quote/StepMaterial";
import StepMounting from "@/components/quote/StepMounting";
import StepFeatures from "@/components/quote/StepFeatures";
import StepContact from "@/components/quote/StepContact";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOTAL_STEPS = 8;

const initialProduct = {
  product_type: "", layout: "", height: "", width: "", depth: "",
  quantity: "1", unit: "metres", material: "", color: "",
  mounting: "", operation: "", track_type: "", features: [], entry_point: "",
};

const initialContact = { name: "", company: "", email: "", phone: "", notes: "" };

const slideVariants = {
  enter: (dir) => ({ x: dir > 0 ? 80 : -80, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? -80 : 80, opacity: 0 }),
};

function fmt(slug) {
  return (slug || "").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function CartItemRow({ item, index, onEdit, onRemove }) {
  return (
    <div data-testid={`cart-item-${index}`} className="bg-[#141414] border border-[#2E2E2E] p-4 sm:p-5 flex items-start gap-4">
      <div className="w-10 h-10 bg-[#FF5722]/10 flex items-center justify-center shrink-0">
        <Package size={18} className="text-[#FF5722]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-white">{fmt(item.product_type)}</p>
        <p className="text-xs text-[#A3A3A3] mt-1">
          {fmt(item.layout)} — {item.height} x {item.width}{item.depth ? ` x ${item.depth}` : ""} {item.unit}
        </p>
        <p className="text-xs text-[#666] mt-0.5">
          {fmt(item.material)}{item.color ? ` / ${item.color}` : ""} — {fmt(item.operation)}
          {item.quantity && item.quantity !== "1" ? ` — Qty: ${item.quantity}` : ""}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button data-testid={`cart-edit-${index}`} onClick={() => onEdit(index)} className="p-2 border border-[#2E2E2E] text-[#A3A3A3] hover:border-[#FF5722] hover:text-[#FF5722] transition-colors" title="Edit">
          <Pencil size={14} />
        </button>
        <button data-testid={`cart-remove-${index}`} onClick={() => onRemove(index)} className="p-2 border border-[#2E2E2E] text-[#A3A3A3] hover:border-red-500 hover:text-red-400 transition-colors" title="Remove">
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

export default function QuotePage() {
  const [step, setStep] = useState(1);
  const [dir, setDir] = useState(1);
  const [loading, setLoading] = useState(false);
  const [product, setProduct] = useState(initialProduct);
  const [contact, setContact] = useState(initialContact);
  const [cartItems, setCartItems] = useState([]);
  const [editingIndex, setEditingIndex] = useState(-1);
  const navigate = useNavigate();

  const canProceed = () => {
    switch (step) {
      case 1: return !!product.product_type;
      case 2: return !!product.layout;
      case 3: return !!product.height && !!product.width;
      case 4: return !!product.material;
      case 5: return !!product.operation;
      case 6: return true;
      case 7: return cartItems.length > 0;
      case 8: return !!contact.name && !!contact.email;
      default: return true;
    }
  };

  const stepErrors = {
    1: "Please select a product type.",
    2: "Please select a layout configuration.",
    3: "Please enter at least height and width.",
    4: "Please select a material.",
    5: "Please select an operation type (Sliding or Fixed).",
    7: "Add at least one item to your quote.",
    8: "Name and email are required.",
  };

  const addToCart = () => {
    if (editingIndex >= 0) {
      setCartItems((prev) => prev.map((it, i) => (i === editingIndex ? { ...product } : it)));
      toast.success("Item updated");
    } else {
      setCartItems((prev) => [...prev, { ...product }]);
      toast.success("Item added to quote");
    }
    setEditingIndex(-1);
    setProduct(initialProduct);
    setDir(1);
    setStep(7);
  };

  const editItem = (index) => {
    setProduct({ ...cartItems[index] });
    setEditingIndex(index);
    setDir(-1);
    setStep(1);
  };

  const removeItem = (index) => {
    setCartItems((prev) => prev.filter((_, i) => i !== index));
    toast.success("Item removed");
  };

  const addAnother = () => {
    setProduct(initialProduct);
    setEditingIndex(-1);
    setDir(-1);
    setStep(1);
  };

  const next = () => {
    if (!canProceed()) {
      toast.error(stepErrors[step] || "Please complete this step.");
      return;
    }
    if (step === 6) { addToCart(); return; }
    setDir(1);
    setStep((s) => Math.min(s + 1, TOTAL_STEPS));
  };

  const prev = () => {
    if (step === 1) {
      if (cartItems.length > 0 || editingIndex >= 0) {
        setEditingIndex(-1);
        setProduct(initialProduct);
        setDir(-1);
        setStep(7);
      }
      return;
    }
    if (step === 7) return;
    setDir(-1);
    setStep((s) => Math.max(s - 1, 1));
  };

  const handleSubmit = async () => {
    if (!contact.name || !contact.email) { toast.error("Name and email are required."); return; }
    if (cartItems.length === 0) { toast.error("Please add at least one item."); return; }
    setLoading(true);
    try {
      const firstItem = cartItems[0];
      await axios.post(`${API}/quotes`, { items: cartItems, ...firstItem, ...contact });
      toast.success("Quote request submitted!");
      navigate("/quote/thank-you");
    } catch {
      toast.error("Failed to submit. Please try again.");
    } finally { setLoading(false); }
  };

  const renderStep = () => {
    switch (step) {
      case 1: return <StepProduct form={product} setForm={setProduct} />;
      case 2: return <StepLayout form={product} setForm={setProduct} />;
      case 3: return <StepDimensions form={product} setForm={setProduct} />;
      case 4: return <StepMaterial form={product} setForm={setProduct} />;
      case 5: return <StepMounting form={product} setForm={setProduct} />;
      case 6: return <StepFeatures form={product} setForm={setProduct} />;
      case 7:
        return (
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: "'Clash Display', sans-serif" }}>
              Your Quote Items
            </h2>
            <p className="text-sm text-[#A3A3A3] mb-8">
              {cartItems.length === 0
                ? "No items yet. Add a product to get started."
                : `${cartItems.length} item${cartItems.length !== 1 ? "s" : ""} in your quote. Add more or continue to submit.`}
            </p>
            <div className="space-y-3 mb-6">
              {cartItems.map((item, i) => (
                <CartItemRow key={i} item={item} index={i} onEdit={editItem} onRemove={removeItem} />
              ))}
            </div>
            <button
              data-testid="add-another-btn"
              onClick={addAnother}
              className="inline-flex items-center gap-2 border border-dashed border-[#2E2E2E] text-[#A3A3A3] px-5 py-3 text-sm font-semibold hover:border-[#FF5722] hover:text-[#FF5722] transition-all w-full justify-center"
            >
              <Plus size={16} /> Add Another Item
            </button>
          </div>
        );
      case 8: return <StepContact form={contact} setForm={setContact} />;
      default: return null;
    }
  };

  const backDisabled = (step === 1 && cartItems.length === 0 && editingIndex < 0) || step === 7;

  return (
    <div data-testid="quote-page">
      {/* Cart Badge */}
      {cartItems.length > 0 && step !== 7 && (
        <button
          data-testid="cart-badge"
          onClick={() => { setDir(1); setStep(7); }}
          className="fixed bottom-24 right-6 z-40 bg-[#FF5722] text-white w-14 h-14 flex items-center justify-center shadow-lg hover:bg-[#E64A19] transition-colors"
        >
          <ShoppingCart size={20} />
          <span className="absolute -top-2 -right-2 bg-white text-[#FF5722] text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full">
            {cartItems.length}
          </span>
        </button>
      )}

      {/* Hero */}
      <section className="relative pt-28 pb-8 sm:pt-36 sm:pb-12 bg-[#141414]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <div className="industrial-line mb-6" />
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1] mb-3" style={{ fontFamily: "'Clash Display', sans-serif" }}>
              Quick Quote <span className="gradient-text">Builder</span>
            </h1>
            <p className="text-sm sm:text-base text-[#A3A3A3] max-w-xl">
              Configure your industrial textile solutions step by step. Add multiple items to your quote before submitting.
            </p>
          </div>
          <ProgressBar currentStep={step} />
        </div>
      </section>

      {/* Step Content */}
      <section className="py-12 sm:py-16 bg-[#0A0A0A]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatePresence mode="wait" custom={dir}>
            <motion.div
              key={step}
              custom={dir}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.25, ease: "easeInOut" }}
            >
              {renderStep()}
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-12 pt-8 border-t border-[#2E2E2E]">
            <button
              type="button"
              data-testid="quote-prev-btn"
              onClick={prev}
              disabled={backDisabled}
              className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold transition-all ${
                backDisabled
                  ? "text-[#666] cursor-not-allowed"
                  : "text-[#A3A3A3] hover:text-white border border-[#2E2E2E] hover:border-[#FF5722]"
              }`}
            >
              <ArrowLeft size={16} /> Back
            </button>

            {step <= 5 ? (
              <button
                type="button"
                data-testid="quote-next-btn"
                onClick={next}
                className={`inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold transition-all ${
                  canProceed() ? "bg-[#FF5722] text-white hover:bg-[#E64A19]" : "bg-[#1F1F1F] text-[#666] cursor-not-allowed"
                }`}
              >
                Next <ArrowRight size={16} />
              </button>
            ) : step === 6 ? (
              <button
                type="button"
                data-testid="quote-add-to-cart-btn"
                onClick={addToCart}
                className="inline-flex items-center gap-2 bg-[#FF5722] text-white px-6 py-2.5 font-semibold text-sm hover:bg-[#E64A19] transition-colors"
              >
                <ShoppingCart size={16} />
                {editingIndex >= 0 ? "Update Item" : "Add to Quote"}
              </button>
            ) : step === 7 ? (
              <button
                type="button"
                data-testid="quote-continue-btn"
                onClick={() => { setDir(1); setStep(8); }}
                disabled={cartItems.length === 0}
                className={`inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold transition-all ${
                  cartItems.length > 0 ? "bg-[#FF5722] text-white hover:bg-[#E64A19]" : "bg-[#1F1F1F] text-[#666] cursor-not-allowed"
                }`}
              >
                Continue to Submit <ArrowRight size={16} />
              </button>
            ) : (
              <button
                type="button"
                data-testid="quote-submit-btn"
                onClick={handleSubmit}
                disabled={loading || !canProceed()}
                className="inline-flex items-center gap-2 bg-[#FF5722] text-white px-8 py-2.5 font-semibold text-sm hover:bg-[#E64A19] transition-colors disabled:opacity-50"
              >
                {loading ? "Submitting..." : (
                  <><Send size={16} /> Submit Quote ({cartItems.length} item{cartItems.length !== 1 ? "s" : ""})</>
                )}
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
